/**
 * Notes Context
 * 
 * Manages clinical notes state to prevent unnecessary re-renders
 * across unrelated features when note data changes.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const NotesContext = createContext();

export const NotesProvider = ({ children }) => {
  // Core notes data
  const [notes, setNotes] = useState([]);
  const [myTodoNotes, setMyTodoNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  
  // Notes filters
  const [notesDateRange, setNotesDateRange] = useState('30');
  const [notesCustomStartDate, setNotesCustomStartDate] = useState('');
  const [notesCustomEndDate, setNotesCustomEndDate] = useState('');
  const [notesClientFilter, setNotesClientFilter] = useState('all');
  const [notesStatusFilter, setNotesStatusFilter] = useState('all');
  
  // UI state - note list
  const [showNoteDetail, setShowNoteDetail] = useState(false);
  const [showCompletedNotes, setShowCompletedNotes] = useState(false);
  const [showActiveNotes, setShowActiveNotes] = useState(false);
  const [noteContextMenu, setNoteContextMenu] = useState({ show: false, x: 0, y: 0, note: null });
  const [noteDetailHeaderCollapsed, setNoteDetailHeaderCollapsed] = useState(false);
  
  // Note editing state
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [editedNoteContentInternal, setEditedNoteContentInternal] = useState(null);
  const [noteJustSigned, setNoteJustSigned] = useState(false);
  const [selectedNoteStatus, setSelectedNoteStatus] = useState('draft');
  const [isSavingNote, setIsSavingNote] = useState(false);  // Prevent double-save
  const [noteCreationError, setNoteCreationError] = useState(null);  // Track note creation failures to keep dialog open
  const [autosaveBlockedReason, setAutosaveBlockedReason] = useState(null);  // Track why autosave is blocked (template/client mismatch)
  
  // Safe setter for editedNoteContent that prevents accidental data loss
  // During active editing, it will log a warning if trying to set empty content
  // Supports both direct values and functional updaters
  //
  // Phase-2 stabilization (Feb 2026): wrapped in useCallback.  The
  // deps are `isEditingNote` and `editedNoteContentInternal` because
  // the empty-content guard reads both from closure.  Functional-
  // updater path is closure-safe (it reads `prev` from the setter
  // argument) so it does NOT need to be in the deps.
  const setEditedNoteContent = useCallback((newContentOrUpdater) => {
    // Support functional updaters: setEditedNoteContent(prev => newContent)
    if (typeof newContentOrUpdater === 'function') {
      setEditedNoteContentInternal(prev => {
        const newContent = newContentOrUpdater(prev);
        // Apply the same safety check
        if (newContent === null) return null;
        if (isEditingNote && prev) {
          const hasOldContent = prev.fields?.length > 0 || prev.sections?.length > 0;
          const hasNewContent = newContent?.fields?.length > 0 || newContent?.sections?.length > 0;
          if (hasOldContent && !hasNewContent) {
            console.warn('[NotesContext] BLOCKED: Functional updater returned empty content during active editing');
            return prev;
          }
        }
        return newContent;
      });
      return;
    }
    
    const newContent = newContentOrUpdater;
    
    // Allow setting to null (for cleanup when closing note)
    if (newContent === null) {
      setEditedNoteContentInternal(null);
      return;
    }
    
    // If we're actively editing and the new content is empty, log warning
    if (isEditingNote && editedNoteContentInternal) {
      const hasOldContent = editedNoteContentInternal.fields?.length > 0 || editedNoteContentInternal.sections?.length > 0;
      const hasNewContent = newContent?.fields?.length > 0 || newContent?.sections?.length > 0;
      
      if (hasOldContent && !hasNewContent) {
        console.warn('[NotesContext] BLOCKED: Attempted to set empty editedNoteContent during active editing');
        return;
      }
    }
    
    setEditedNoteContentInternal(newContent);
  }, [isEditingNote, editedNoteContentInternal]);
  
  // Expose the internal state as editedNoteContent
  const editedNoteContent = editedNoteContentInternal;
  
  // Bulk selection
  const [selectedNotes, setSelectedNotes] = useState([]); // Main Notes tab
  const [selectedClinicalNotes, setSelectedClinicalNotes] = useState([]); // Client dialog Clinical Notes tab
  
  // Note creation/add form
  const [showAddNote, setShowAddNote] = useState(false);
  const [showCreateNoteDialog, setShowCreateNoteDialog] = useState(false);
  const [createNoteClientId, setCreateNoteClientId] = useState('');
  const [createNoteAppointmentId, setCreateNoteAppointmentId] = useState('');
  const [selectedNoteProvider, setSelectedNoteProvider] = useState(null);
  const [selectedNoteClients, setSelectedNoteClients] = useState([]);
  const [noteDialogHeaderCollapsed, setNoteDialogHeaderCollapsed] = useState(false);
  const [noteClientLocked, setNoteClientLocked] = useState(false);
  const [selectedNoteGroupId, setSelectedNoteGroupId] = useState(null);
  const [newNoteFormData, setNewNoteFormData] = useState({});
  
  // Note review/billing
  const [notesReadyForBilling, setNotesReadyForBilling] = useState([]);
  const [selectedNoteForReview, setSelectedNoteForReview] = useState(null);
  const [noteReviewData, setNoteReviewData] = useState(null);
  const [showNoteReviewDialog, setShowNoteReviewDialog] = useState(false);
  const [notesSentForCorrection, setNotesSentForCorrection] = useState([]);
  
  // Pagination
  const [activeNotesPage, setActiveNotesPage] = useState(1);
  const [activeNotesPageSize, setActiveNotesPageSize] = useState(5);
  const [billedNotesPage, setBilledNotesPage] = useState(1);
  const [billedNotesPageSize, setBilledNotesPageSize] = useState(5);
  
  // Pending deletes (for undo functionality)
  const [pendingDeletes, setPendingDeletes] = useState({});

  // Phase-2 stabilization (Feb 2026): wrap `resetNoteForm` in useCallback
  // with [] — only calls setters from useState (stable refs).
  const resetNoteForm = useCallback(() => {
    setNewNoteFormData({});
    setCreateNoteClientId('');
    setCreateNoteAppointmentId('');
    setSelectedNoteProvider(null);
    setSelectedNoteClients([]);
    setSelectedNoteGroupId(null);
    setNoteClientLocked(false);
  }, []);

  // Phase-2 stabilization (Feb 2026): memoize the Provider value.
  // This is the per-keystroke hot path — every character typed in a
  // note triggers `setEditedNoteContent`, which would otherwise force
  // every consumer of useNotes() to re-render (Dashboard + every
  // note-related UI surface).  Now the memo returns a stable ref
  // unless one of the listed fields actually changed.
  //
  // Setters from useState are stable across renders — intentionally
  // NOT in the deps array.  `setEditedNoteContent` and `resetNoteForm`
  // are now useCallback-stable above.
  const value = useMemo(() => ({
    // Core data
    notes,
    setNotes,
    myTodoNotes,
    setMyTodoNotes,
    selectedNote,
    setSelectedNote,
    
    // Filters
    notesDateRange,
    setNotesDateRange,
    notesCustomStartDate,
    setNotesCustomStartDate,
    notesCustomEndDate,
    setNotesCustomEndDate,
    notesClientFilter,
    setNotesClientFilter,
    notesStatusFilter,
    setNotesStatusFilter,
    
    // UI state - note list
    showNoteDetail,
    setShowNoteDetail,
    showCompletedNotes,
    setShowCompletedNotes,
    showActiveNotes,
    setShowActiveNotes,
    noteContextMenu,
    setNoteContextMenu,
    noteDetailHeaderCollapsed,
    setNoteDetailHeaderCollapsed,
    
    // Note editing
    isEditingNote,
    setIsEditingNote,
    editedNoteContent,
    setEditedNoteContent,
    noteJustSigned,
    setNoteJustSigned,
    selectedNoteStatus,
    setSelectedNoteStatus,
    isSavingNote,
    setIsSavingNote,
    noteCreationError,
    setNoteCreationError,
    autosaveBlockedReason,
    setAutosaveBlockedReason,
    
    // Bulk selection
    selectedNotes,
    setSelectedNotes,
    selectedClinicalNotes,
    setSelectedClinicalNotes,
    
    // Note creation/add form
    showAddNote,
    setShowAddNote,
    showCreateNoteDialog,
    setShowCreateNoteDialog,
    createNoteClientId,
    setCreateNoteClientId,
    createNoteAppointmentId,
    setCreateNoteAppointmentId,
    selectedNoteProvider,
    setSelectedNoteProvider,
    selectedNoteClients,
    setSelectedNoteClients,
    noteDialogHeaderCollapsed,
    setNoteDialogHeaderCollapsed,
    noteClientLocked,
    setNoteClientLocked,
    selectedNoteGroupId,
    setSelectedNoteGroupId,
    newNoteFormData,
    setNewNoteFormData,
    
    // Note review/billing
    notesReadyForBilling,
    setNotesReadyForBilling,
    selectedNoteForReview,
    setSelectedNoteForReview,
    noteReviewData,
    setNoteReviewData,
    showNoteReviewDialog,
    setShowNoteReviewDialog,
    notesSentForCorrection,
    setNotesSentForCorrection,
    
    // Pagination
    activeNotesPage,
    setActiveNotesPage,
    activeNotesPageSize,
    setActiveNotesPageSize,
    billedNotesPage,
    setBilledNotesPage,
    billedNotesPageSize,
    setBilledNotesPageSize,
    
    // Pending deletes
    pendingDeletes,
    setPendingDeletes,
    
    // Actions
    resetNoteForm,
  }), [
    // Core
    notes, myTodoNotes, selectedNote,
    // Filters
    notesDateRange, notesCustomStartDate, notesCustomEndDate,
    notesClientFilter, notesStatusFilter,
    // List UI
    showNoteDetail, showCompletedNotes, showActiveNotes,
    noteContextMenu, noteDetailHeaderCollapsed,
    // Editing
    isEditingNote, editedNoteContent, noteJustSigned,
    selectedNoteStatus, isSavingNote, noteCreationError,
    autosaveBlockedReason,
    // Bulk
    selectedNotes, selectedClinicalNotes,
    // Create form
    showAddNote, showCreateNoteDialog,
    createNoteClientId, createNoteAppointmentId,
    selectedNoteProvider, selectedNoteClients,
    noteDialogHeaderCollapsed, noteClientLocked,
    selectedNoteGroupId, newNoteFormData,
    // Review/billing
    notesReadyForBilling, selectedNoteForReview, noteReviewData,
    showNoteReviewDialog, notesSentForCorrection,
    // Pagination
    activeNotesPage, activeNotesPageSize,
    billedNotesPage, billedNotesPageSize,
    // Pending deletes
    pendingDeletes,
    // Stable callbacks (already memoized — included for correctness)
    setEditedNoteContent, resetNoteForm,
  ]);

  return (
    <NotesContext.Provider value={value}>
      {children}
    </NotesContext.Provider>
  );
};

export const useNotes = () => {
  const context = useContext(NotesContext);
  if (!context) {
    throw new Error('useNotes must be used within NotesProvider');
  }
  return context;
};

export default NotesContext;
